import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/lib/auth';
import { LoadingState } from '@/components/states';
import { LoginPage } from '@/routes/LoginPage';
import { useT } from '@/i18n';
import { Page, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const lazy = <T extends Record<string, React.ComponentType>>(f: () => Promise<T>, name: keyof T) =>
  React.lazy(() => f().then((m) => ({ default: m[name] as React.ComponentType })));

const DashboardPage = lazy(() => import('@/routes/DashboardPage'), 'DashboardPage');
const CopyPracticePage = lazy(() => import('@/routes/practice/CopyPracticePage'), 'CopyPracticePage');
const RecallPracticePage = lazy(() => import('@/routes/practice/RecallPracticePage'), 'RecallPracticePage');
const FreePracticePage = lazy(() => import('@/routes/practice/FreePracticePage'), 'FreePracticePage');
const LibraryPage = lazy(() => import('@/routes/LibraryPage'), 'LibraryPage');
const LibraryDetailPage = lazy(() => import('@/routes/LibraryDetailPage'), 'LibraryDetailPage');
const HistoryPage = lazy(() => import('@/routes/HistoryPage'), 'HistoryPage');
const ResultPage = lazy(() => import('@/routes/ResultPage'), 'ResultPage');
const ProgressPage = lazy(() => import('@/routes/ProgressPage'), 'ProgressPage');
const ExamPage = lazy(() => import('@/routes/exam/ExamPage'), 'ExamPage');
const ExamSessionPage = lazy(() => import('@/routes/exam/ExamSessionPage'), 'ExamSessionPage');
const SharedListPage = lazy(() => import('@/routes/SharedListPage'), 'SharedListPage');
const SharedPage = lazy(() => import('@/routes/SharedPage'), 'SharedPage');
const SettingsPage = lazy(() => import('@/routes/SettingsPage'), 'SettingsPage');
const AdminPage = lazy(() => import('@/routes/AdminPage'), 'AdminPage');
const WritePage = lazy(() => import('@/routes/WritePage'), 'WritePage');

function Fallback() {
  return (
    <div className="p-8">
      <LoadingState rows={2} />
    </div>
  );
}

/** Catches render errors and failed lazy chunks (e.g. an old tab after a deploy) instead of a white screen. */
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  override componentDidCatch(error: Error) {
    const chunk =
      /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
        error.message,
      );
    if (chunk) {
      try {
        if (!sessionStorage.getItem('gn.chunkReload')) {
          sessionStorage.setItem('gn.chunkReload', '1');
          window.location.reload();
        }
      } catch {
        /* storage unavailable */
      }
    }
  }
  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center">
        <h1 className="mb-2 text-xl font-semibold">Что-то пошло не так · Something went wrong</h1>
        <p className="mb-6 text-sm text-muted">
          Черновики сохранены на устройстве. Drafts are kept on this device.
        </p>
        <Button onClick={() => window.location.reload()}>Обновить · Reload</Button>
      </div>
    );
  }
}

function NotFound() {
  const t = useT();
  return (
    <Page>
      <PageHeader title="404" subtitle={t('err.NOT_FOUND')} />
      <Button asChild variant="secondary">
        <Link to="/">{t('nav.home')}</Link>
      </Button>
    </Page>
  );
}

function ScrollReset() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    document.getElementById('main')?.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <ScrollReset />
      <ErrorBoundary>
        <React.Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/write/:sessionId"
              element={
                <RequireAuth>
                  <WritePage />
                </RequireAuth>
              }
            />
            <Route
              path="/exam/:examId"
              element={
                <RequireAuth>
                  <ExamSessionPage />
                </RequireAuth>
              }
            />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="practice" element={<Navigate to="/practice/copy" replace />} />
              <Route path="practice/copy" element={<CopyPracticePage />} />
              <Route path="practice/recall" element={<RecallPracticePage />} />
              <Route path="practice/free" element={<FreePracticePage />} />
              <Route path="exam" element={<ExamPage />} />
              <Route path="library" element={<LibraryPage />} />
              <Route path="library/:id" element={<LibraryDetailPage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="history/:id" element={<ResultPage />} />
              <Route path="progress" element={<ProgressPage />} />
              <Route path="shared" element={<SharedListPage />} />
              <Route path="shared/:id" element={<SharedPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route
                path="admin"
                element={
                  <RequireAuth role="admin">
                    <AdminPage />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
